/**
 * ThirdPartySpawnModal Component
 *
 * Modal for adding pre-colonised spawn purchased from external suppliers.
 * Creates a spawn_batch with is_third_party=true and status='colonised'.
 */

import { useState, useEffect, useMemo } from 'react'
import { toLocalDateString } from '@/utils/dateUtils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useStrains } from '@/hooks/useLC'
import { useSuppliers } from '@/hooks/useSuppliers'
import { useSpawnMutations } from '@/hooks/useSpawnBatches'
import SupplierDialog from '@/components/inventory/SupplierDialog'
import { Package, Loader2, Plus } from 'lucide-react'

// Available grain types for spawn
const GRAIN_TYPES = [
  { value: 'millet', label: 'Millet' },
  { value: 'rye', label: 'Rye' },
  { value: 'wheat', label: 'Wheat' },
  { value: 'oat', label: 'Oat' },
  { value: 'sorghum', label: 'Sorghum' },
  { value: 'other', label: 'Other' }
]

export default function ThirdPartySpawnModal({
  open,
  onOpenChange,
  onSave
}) {
  // Form state
  const [supplierId, setSupplierId] = useState('')
  const [supplierCode, setSupplierCode] = useState('')
  const [strainId, setStrainId] = useState('')
  const [grainType, setGrainType] = useState('')
  const [bagCount, setBagCount] = useState('')
  const [weightPerBag, setWeightPerBag] = useState('')
  const [totalCost, setTotalCost] = useState('')
  const [dateReceived, setDateReceived] = useState('')
  const [notes, setNotes] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(null)
  const [showSupplierDialog, setShowSupplierDialog] = useState(false)
  const [existingSupplierIds, setExistingSupplierIds] = useState(new Set())

  // Hooks
  const { strains } = useStrains()
  const { suppliers, refetch: refetchSuppliers } = useSuppliers()
  const { createSpawnBatch, loading: mutationLoading } = useSpawnMutations()

  // Calculate cost per bag
  const costPerBag = useMemo(() => {
    const bags = parseFloat(bagCount) || 0
    const cost = parseFloat(totalCost) || 0
    if (bags > 0 && cost > 0) {
      return (cost / bags).toFixed(2)
    }
    return null
  }, [bagCount, totalCost])

  // Calculate total weight
  const totalWeight = useMemo(() => {
    const bags = parseFloat(bagCount) || 0
    const weight = parseFloat(weightPerBag) || 0
    if (bags > 0 && weight > 0) {
      return (bags * weight).toFixed(1)
    }
    return null
  }, [bagCount, weightPerBag])

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setSupplierId('')
      setSupplierCode('')
      setStrainId('')
      setGrainType('')
      setBagCount('')
      setWeightPerBag('')
      setTotalCost('')
      setDateReceived(toLocalDateString(new Date()))
      setNotes('')
      setError(null)
    }
  }, [open])

  // Generate batch code: YYMMDD + StrainCode + "-" + SupplierCode
  const generateBatchCode = () => {
    const selectedStrain = strains.find(s => s.id === strainId)
    if (!dateReceived || !selectedStrain || !supplierCode.trim()) {
      return null
    }

    // Format date as YYMMDD
    const date = new Date(dateReceived)
    const yy = String(date.getFullYear()).slice(-2)
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const dd = String(date.getDate()).padStart(2, '0')

    return `${yy}${mm}${dd}${selectedStrain.code}-${supplierCode.trim().toUpperCase()}`
  }

  // Handle save
  const handleSave = async () => {
    // Validation
    if (!supplierId) {
      setError('Supplier is required')
      return
    }
    if (!supplierCode.trim()) {
      setError('Supplier code is required')
      return
    }
    if (!strainId) {
      setError('Strain is required')
      return
    }
    if (!grainType) {
      setError('Grain type is required')
      return
    }
    if (!bagCount || parseFloat(bagCount) <= 0) {
      setError('Number of bags must be greater than 0')
      return
    }
    if (!weightPerBag || parseFloat(weightPerBag) <= 0) {
      setError('Weight per bag must be greater than 0')
      return
    }
    if (!dateReceived) {
      setError('Date received is required')
      return
    }

    setIsSaving(true)
    setError(null)

    try {
      const batchCode = generateBatchCode()

      const spawnData = {
        code: batchCode,
        strain_id: strainId,
        is_third_party: true,
        supplier_id: supplierId,
        status: 'colonised', // Third-party spawn arrives ready to use
        grain_type: grainType,
        bag_count: parseInt(bagCount, 10),
        bag_weight_kg: parseFloat(weightPerBag),
        total_cost: totalCost ? parseFloat(totalCost) : null,
        cost_per_bag: costPerBag ? parseFloat(costPerBag) : null,
        inoculation_date: dateReceived || null,
        expected_ready_date: dateReceived || null, // Already ready
        notes: notes.trim() || null
      }

      const savedBatch = await createSpawnBatch(spawnData)
      onSave?.(savedBatch)
      onOpenChange(false)
    } catch (err) {
      console.error('[ThirdPartySpawnModal] Save error:', err)
      setError(err.message || 'Failed to save spawn inventory')
    } finally {
      setIsSaving(false)
    }
  }

  // Handle cancel
  const handleCancel = () => {
    onOpenChange(false)
  }

  // Handle supplier created - auto-select the new supplier
  const handleSupplierCreated = async () => {
    // Refetch suppliers to get the new one
    await refetchSuppliers()
    setShowSupplierDialog(false)
  }

  // Effect to auto-select new supplier after refetch
  useEffect(() => {
    if (existingSupplierIds.size > 0 && suppliers.length > existingSupplierIds.size) {
      // Find the supplier that wasn't in the existing set
      const newSupplier = suppliers.find(s => !existingSupplierIds.has(s.id))
      if (newSupplier) {
        setSupplierId(newSupplier.id)
        setExistingSupplierIds(new Set()) // Reset tracking
      }
    }
  }, [suppliers, existingSupplierIds])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Add Spawn Inventory
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 space-y-4 -mx-6 px-6 py-2">
          {/* Supplier and Supplier Code */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="supplier">
                Supplier <span className="text-destructive">*</span>
              </Label>
              <Select
                value={supplierId}
                onValueChange={(value) => {
                  if (value === '__create_new__') {
                    // Track existing supplier IDs before opening dialog
                    setExistingSupplierIds(new Set(suppliers.map(s => s.id)))
                    setShowSupplierDialog(true)
                  } else {
                    setSupplierId(value)
                  }
                }}
                disabled={isSaving}
              >
                <SelectTrigger id="supplier">
                  <SelectValue placeholder="Select supplier..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__create_new__">
                    <span className="flex items-center gap-2 text-primary">
                      <Plus className="h-4 w-4" />
                      Create new supplier
                    </span>
                  </SelectItem>
                  {suppliers.map(supplier => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="supplier-code">
                Supplier Code <span className="text-destructive">*</span>
              </Label>
              <Input
                id="supplier-code"
                value={supplierCode}
                onChange={(e) => setSupplierCode(e.target.value.toUpperCase())}
                placeholder="e.g., JCM"
                maxLength={10}
                disabled={isSaving}
                className="uppercase"
              />
            </div>
          </div>

          {/* Strain */}
          <div className="space-y-2">
            <Label htmlFor="strain">
              Strain <span className="text-destructive">*</span>
            </Label>
            <Select
              value={strainId}
              onValueChange={setStrainId}
              disabled={isSaving}
            >
              <SelectTrigger id="strain">
                <SelectValue placeholder="Select strain..." />
              </SelectTrigger>
              <SelectContent>
                {strains.map(strain => (
                  <SelectItem key={strain.id} value={strain.id}>
                    {strain.name} ({strain.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Grain Type */}
          <div className="space-y-2">
            <Label htmlFor="grain-type">
              Grain Type <span className="text-destructive">*</span>
            </Label>
            <Select
              value={grainType}
              onValueChange={setGrainType}
              disabled={isSaving}
            >
              <SelectTrigger id="grain-type">
                <SelectValue placeholder="Select grain type..." />
              </SelectTrigger>
              <SelectContent>
                {GRAIN_TYPES.map(grain => (
                  <SelectItem key={grain.value} value={grain.value}>
                    {grain.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Quantity and Weight Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="bag-count">
                Number of Bags <span className="text-destructive">*</span>
              </Label>
              <Input
                id="bag-count"
                type="number"
                value={bagCount}
                onChange={(e) => setBagCount(e.target.value)}
                placeholder="0"
                min="1"
                disabled={isSaving}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="weight-per-bag">
                Weight per Bag <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="weight-per-bag"
                  type="number"
                  value={weightPerBag}
                  onChange={(e) => setWeightPerBag(e.target.value)}
                  placeholder="0.0"
                  min="0"
                  step="0.1"
                  disabled={isSaving}
                  className="pr-8"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  kg
                </span>
              </div>
            </div>
          </div>

          {/* Total weight display */}
          {totalWeight && (
            <p className="text-sm text-muted-foreground">
              Total: {totalWeight} kg
            </p>
          )}

          {/* Cost and Date Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="total-cost">
                Total Cost <span className="text-muted-foreground">(optional)</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  £
                </span>
                <Input
                  id="total-cost"
                  type="number"
                  value={totalCost}
                  onChange={(e) => setTotalCost(e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  disabled={isSaving}
                  className="pl-7"
                />
              </div>
              {costPerBag && (
                <p className="text-xs text-muted-foreground">
                  £{costPerBag} per bag
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="date-received">
                Date Received
              </Label>
              <Input
                id="date-received"
                type="date"
                value={dateReceived}
                onChange={(e) => setDateReceived(e.target.value)}
                disabled={isSaving}
              />
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">
              Notes <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes about this spawn..."
              rows={2}
              disabled={isSaving}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4 mt-2">
          <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || mutationLoading}>
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Add Inventory
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Supplier Creation Dialog */}
      <SupplierDialog
        open={showSupplierDialog}
        onOpenChange={setShowSupplierDialog}
        supplier={null}
        onSuccess={handleSupplierCreated}
      />
    </Dialog>
  )
}
